import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  Client,
  ClientInput,
  ClientRateCardInput,
  ClientStatus,
  Invite,
  User,
} from '../generated/gql-types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { clientActions } from './client.action';
import { upsertMany, upsertOne } from '../util/zustand.util';

interface ClientState {
  clients: Client[];
  hasMore: boolean;
  lastEvaluatedKey: string | null;
  users: User[];
  invites: Invite[];
  getClientById: (id: string) => Client | null;
}

type ClientStore = ClientState & {
  setClients: (clients: Client[]) => void;
  upsertClients: (clients: Client[]) => void;
  fetchClientsForTenant: (
    tenantId?: string,
    limit?: number,
    lastEvaluatedKey?: string,
    forceFetchAll?: boolean,
  ) => Promise<{ clients: Client[] }>;
  createClient: (
    details: ClientInput,
  ) => Promise<{ success: boolean; client?: Client; error?: string }>;
  fetchClientById: (id: string) => Promise<Client | null>;
  fetchUsersByClient: (clientId: string) => Promise<User[]>;
  fetchInvitesForClient: (clientId: string) => Promise<Invite[]>;
  getClientById: (id: string) => Client | null;
  updateClient: (
    id: string,
    updatedClient: Partial<Client>,
  ) => Promise<{ success: boolean; client?: Client; error?: string }>;
  setClientStatus: (
    id: string,
    status: ClientStatus,
  ) => Promise<{ success: boolean; client?: Client; error?: string }>;
  setClientRateCard: (
    clientId: string,
    rateCard: ClientRateCardInput,
  ) => Promise<{ success: boolean; client?: Client; error?: string }>;
  revokeInvite: (id: string) => Promise<{ success: boolean; error?: string }>;
};

const validateClientInput = (details: ClientInput): string | null => {
  if (!details.name?.trim()) {
    return 'Client name is required';
  }
  if (!details.tenantId?.trim()) {
    return 'Tenant ID is required';
  }
  return null;
};

export const useClientStore = create<ClientStore>()(
  persist(
    (set, get) => ({
      // States
      clients: [],
      users: [],
      invites: [],
      hasMore: true,
      lastEvaluatedKey: null,

      // Setters
      setClients: (setClients: Client[]) => set({ clients: setClients }),
      upsertClients: (clients: Client[]) => {
        set((state) => ({
          clients: upsertMany(state.clients, clients),
        }));
      },

      // Actions
      fetchClientsForTenant: async (
        tenantId?: string,
        limit: number = 20,
        lastEvaluatedKey?: string,
        forceFetchAll?: boolean,
      ) => {
        try {
          const result = await clientActions.getAllClientsForTenant(
            tenantId,
            limit,
            lastEvaluatedKey,
            forceFetchAll,
          );
          const clients = result.items;

          set((state) => ({
            clients: forceFetchAll
              ? clients
              : upsertMany(state.clients, clients),
            hasMore: result.hasMore,
            lastEvaluatedKey: result.lastEvaluatedKey,
          }));

          return { clients };
        } catch (error) {
          console.error('error', error);
          return { clients: [], total: 0 };
        }
      },
      createClient: async (details: ClientInput) => {
        try {
          // Validate input
          const validationError = validateClientInput(details);
          if (validationError) {
            return { success: false, error: validationError };
          }

          // Sanitize input
          const sanitizedDetails: ClientInput = {
            ...details,
            name: details.name?.trim(),
            tenantId: details.tenantId?.trim(),
            address: details.address
              ? {
                  ...details.address,
                  addressLine1: details.address.addressLine1?.trim(),
                  addressLine2: details.address.addressLine2?.trim(),
                  suburb: details.address.suburb?.trim(),
                  state: details.address.state?.trim(),
                  postcode: details.address.postcode?.trim(),
                  countryLabel: details.address.countryLabel?.trim(),
                  countryValue: details.address.countryValue?.trim(),
                }
              : undefined,
          };

          const result = await clientActions.createClient(sanitizedDetails);

          if (!result.success) {
            return result;
          }

          set((state) => ({
            clients: [result.client!, ...state.clients],
          }));
          return result;
        } catch (error) {
          console.error('Error creating client:', error);
          return {
            success: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };
        }
      },
      fetchClientById: async (id: string): Promise<Client | null> => {
        try {
          const client = await clientActions.getClientById(id);
          set((state) => {
            if (client) {
              const clients = upsertOne(state.clients, client);
              return {
                clients: clients,
              };
            }
            return {
              clients: state.clients,
            };
          });
          return client;
        } catch (error) {
          console.error('Error fetching client:', error);
          return null;
        }
      },
      fetchUsersByClient: async (clientId: string) => {
        try {
          const users = await clientActions.getUsersByClient(clientId);
          set(() => ({
            users,
          }));
          return users;
        } catch (error) {
          console.error('Error fetching users by client:', error);
          return [];
        }
      },
      fetchInvitesForClient: async (clientId: string) => {
        try {
          const invites =
            await clientActions.getOutstandingInvitesForClient(clientId);
          set(() => ({
            invites,
          }));
          return invites;
        } catch (error) {
          console.error('Error fetching invites for client:', error);
          return [];
        }
      },
      updateClient: async (id: string, details: Partial<Client>) => {
        try {
          // Validate input
          if (!id?.trim()) {
            return { success: false, error: 'Client ID is required' };
          }

          const sanitizedDetails = {
            ...details,
            name: details.name?.trim(),
            address: details.address
              ? {
                  addressLine1: details.address.addressLine1?.trim(),
                  addressLine2: details.address.addressLine2?.trim(),
                  suburb: details.address.suburb?.trim(),
                  state: details.address.state?.trim(),
                  postcode: details.address.postcode?.trim(),
                  countryLabel: details.address.countryLabel?.trim(),
                  countryValue: details.address.countryValue?.trim(),
                }
              : undefined,
          };

          const result = await clientActions.updateClient(id, sanitizedDetails);

          if (!result.success) {
            return result;
          }

          // Only update store if update was successful
          if (result.client) {
            set((state) => ({
              clients: upsertOne(state.clients, result.client!),
            }));
          }

          return result;
        } catch (error) {
          console.error('Error updating client:', error);
          return {
            success: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };
        }
      },
      setClientStatus: async (id: string, status: ClientStatus) => {
        try {
          // Validate input
          if (!id?.trim()) {
            return { success: false, error: 'Client ID is required' };
          }

          const result = await clientActions.setClientStatus(id, status);

          if (!result.success) {
            return result;
          }

          // Only update store if status update was successful
          if (result.client) {
            set((state) => ({
              clients: upsertOne(state.clients, result.client!),
            }));
          }

          return result;
        } catch (error) {
          console.error('Error setting client status:', error);
          return {
            success: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };
        }
      },

      setClientRateCard: async (
        clientId: string,
        rateCard: ClientRateCardInput,
      ) => {
        try {
          if (!clientId?.trim()) {
            return { success: false, error: 'Client ID is required' };
          }

          const result = await clientActions.setClientRateCard(
            clientId,
            rateCard,
          );

          if (!result.success) {
            return result;
          }

          if (result.client) {
            set((state) => ({
              clients: upsertOne(state.clients, result.client!),
            }));
          }

          return result;
        } catch (error) {
          console.error('Error setting client rate card:', error);
          return {
            success: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };
        }
      },

      revokeInvite: async (id: string) => {
        try {
          const result = await clientActions.revokeInvite(id);

          if (!result.success) {
            return result;
          }

          // Remove the invite from the store
          set((state) => ({
            invites: state.invites.filter((invite) => invite.id !== id),
          }));

          return { success: true };
        } catch (error) {
          console.error('Error revoking invite:', error);
          return {
            success: false,
            error:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
          };
        }
      },

      // Getters
      getClientById: (id: string) =>
        get().clients.find((client) => client?.id === id) || null,
    }),
    {
      name: 'client-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state: ClientStore) => ({
        clients: state.clients,
      }),
    },
  ) as any,
);
